import { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Button,
	Typography,
	Divider,
	IconButton,
	Tooltip,
	Stack,
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import HealthScoreWeightingSkeleton from './HealthScoreWeightingSkeleton';
import HealthScoreWeightingConfirmModal from './HealthScoreWeightingConfirmModal';
import PrimaryButton from '../Common/Buttons/PrimaryButton';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';
import { useUserContext } from 'contexts/UserContext';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import healthScoreWeightingService from 'services/healthScoreWeightingService';
import {
	DEFAULT_WEIGHTS,
	MAX_WEIGHT,
	MIN_WEIGHT,
	REQUIRED_TOTAL,
	WEIGHTING_ALERTS_DISCLAIMER,
	WEIGHTING_DISCLAIMER,
	WEIGHT_SIGNALS,
	WEIGHT_STEP,
	normalizeWeights,
	sumWeights,
} from './healthScoreWeightingConstants';
import { getHealthScoreWeightingErrorMessage } from './healthScoreWeightingErrors';

const TOOLTIP_COPY = `${WEIGHTING_DISCLAIMER} ${WEIGHTING_ALERTS_DISCLAIMER}`;

const INVALID_TOTAL_MESSAGE = 'Weights must total 100% before saving';

const stepperButtonSx = {
	backgroundColor: 'background.paper',
	border: '0.5px solid',
	borderColor: 'border.main',
	borderRadius: '6px',
	padding: '3px',
	'&:hover': { backgroundColor: 'background.paper' },
};

const HealthScoreWeighting = () => {
	const { tenant, loading: tenantLoading } = useIRWorkstation();
	const { user } = useUserContext();
	const { showNotification } = useNotificationContext();
	const { trackButtonClick } = useTrackEvent({ tenant, product: 'IEM' });

	const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
	const [savedWeights, setSavedWeights] = useState(DEFAULT_WEIGHTS);
	const [weightsLoading, setWeightsLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [saving, setSaving] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const canEdit = user?.role === 'tenant_admin';
	const total = sumWeights(weights);
	const isTotalValid = total === REQUIRED_TOTAL;
	const hasChanges = WEIGHT_SIGNALS.some(
		({ key }) => weights[key] !== savedWeights[key]
	);

	// Load the tenant's weights and establish the baseline for unsaved changes.
	// A failed load must NOT fall back to the default weights — they would
	// render as if they were the tenant's own, and a PATCH from that baseline
	// could overwrite real values after a transient error
	const fetchWeights = useCallback(async () => {
		if (!tenant?.id) {
			setWeightsLoading(false);
			return;
		}

		setWeightsLoading(true);
		setLoadError(null);

		try {
			const response =
				await healthScoreWeightingService.getHealthScoreWeighting(tenant.id);
			const value = normalizeWeights(response?.weights);

			setWeights(value);
			setSavedWeights(value);
		} catch (err) {
			const message = getHealthScoreWeightingErrorMessage(err, 'load');

			setLoadError(message);
			showNotification(message, 'error');
		} finally {
			setWeightsLoading(false);
		}
	}, [tenant?.id, showNotification]);

	useEffect(() => {
		fetchWeights();
	}, [fetchWeights]);

	// The card must not show the default weights while the tenant is still
	// resolving, or the fetched values would flash over them on every reload
	const loading = tenantLoading || weightsLoading;

	const adjustWeight = (key, delta) => {
		setWeights((current) => {
			const next = current[key] + delta;

			if (next < MIN_WEIGHT || next > MAX_WEIGHT) {
				return current;
			}

			return { ...current, [key]: next };
		});
	};

	const handleSaveClick = () => {
		if (!canEdit || saving || !hasChanges || !isTotalValid) {
			return;
		}

		trackButtonClick(ButtonClickTargets.SettingsHealthScoreWeightingSave);
		setConfirmOpen(true);
	};

	const handleCancel = () => {
		trackButtonClick(ButtonClickTargets.SettingsHealthScoreWeightingCancel);
		// Cancel only dismisses the modal; the edited weights stay in place
		setConfirmOpen(false);
	};

	const handleReset = () => {
		trackButtonClick(ButtonClickTargets.SettingsHealthScoreWeightingReset);
		setWeights(savedWeights);
	};

	const handleConfirmAndSave = async () => {
		if (!tenant?.id || saving) {
			return;
		}

		trackButtonClick(
			ButtonClickTargets.SettingsHealthScoreWeightingConfirmAndSave
		);
		setSaving(true);

		try {
			const response =
				await healthScoreWeightingService.updateHealthScoreWeighting(
					tenant.id,
					weights
				);

			const value = normalizeWeights(response?.weights);

			setWeights(value);
			setSavedWeights(value);
			setConfirmOpen(false);

			showNotification('Health score weighting saved successfully', 'success');
		} catch (err) {
			showNotification(
				getHealthScoreWeightingErrorMessage(err, 'save'),
				'error'
			);
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return <HealthScoreWeightingSkeleton showButton={canEdit} />;
	}

	const saveLabel = 'Save changes';

	return (
		<Box
			sx={{
				backgroundColor: 'background.paper',
				border: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '8px',
				overflow: 'hidden',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: '16px',
					padding: '10px 12px',
				}}
			>
				<Box>
					<Typography
						sx={{
							fontSize: '13px',
							fontWeight: 600,
							color: 'text.primary',
						}}
					>
						Health Score Weighting
						<Tooltip title={TOOLTIP_COPY}>
							<IconButton
								component='span'
								size='small'
								aria-label={TOOLTIP_COPY}
								data-testid='health-score-weighting-info-icon'
								sx={{
									p: 0,
									ml: '4px',
									display: 'inline-flex',
									verticalAlign: 'middle',
								}}
							>
								<InfoOutlined sx={{ fontSize: 14 }} />
							</IconButton>
						</Tooltip>
					</Typography>

					<Typography
						sx={{
							fontSize: '11px',
							color: 'text.secondary',
							mt: '2px',
						}}
					>
						Adjust how much each signal-based justification contributes to the
						Relationship Health Score
					</Typography>
				</Box>

				<Stack
					direction='row'
					alignItems='center'
					spacing={0.75}
					sx={{ flexShrink: 0 }}
				>
					{canEdit && !loadError && (
						<>
							{hasChanges && (
								<Button
									variant='outlined'
									size='small'
									onClick={handleReset}
									sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
								>
									Reset
								</Button>
							)}

							<PrimaryButton
								label={saveLabel}
								onClick={handleSaveClick}
								disabled={!hasChanges || !isTotalValid}
							/>
						</>
					)}
				</Stack>
			</Box>

			<Divider />

			<Box sx={{ padding: '12px' }}>
				{loadError ? (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: '12px',
							backgroundColor: 'status.red.bg',
							borderRadius: '7px',
							padding: '10px 12px',
						}}
					>
						<Typography sx={{ fontSize: '12px', color: 'status.red.text' }}>
							{loadError}
						</Typography>

						<Button
							variant='outlined'
							size='small'
							onClick={fetchWeights}
							sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
						>
							Retry
						</Button>
					</Box>
				) : (
					<>
						{WEIGHT_SIGNALS.map(({ key, title, description }) => (
							<Box
								key={key}
								sx={{
									display: 'flex',
									alignItems: 'flex-start',
									justifyContent: 'space-between',
									gap: '16px',
									backgroundColor: 'background.subtle',
									borderRadius: '7px',
									padding: '10px 12px',
									mb: '8px',
								}}
							>
								<Box>
									<Typography
										sx={{
											fontSize: '12px',
											fontWeight: 600,
											color: 'text.primary',
										}}
									>
										{title}
									</Typography>

									<Typography
										sx={{
											fontSize: '11px',
											color: 'text.secondary',
											mt: '2px',
										}}
									>
										{description}
									</Typography>
								</Box>

								<Stack
									direction='row'
									alignItems='center'
									spacing={0.5}
									sx={{ flexShrink: 0 }}
								>
									<IconButton
										size='small'
										aria-label={`Decrease ${title} weight`}
										onClick={() => adjustWeight(key, -WEIGHT_STEP)}
										disabled={!canEdit || weights[key] <= MIN_WEIGHT}
										sx={stepperButtonSx}
									>
										<Remove sx={{ fontSize: 13 }} />
									</IconButton>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 700,
											minWidth: '24px',
											textAlign: 'center',
										}}
									>
										{weights[key]}
									</Typography>

									<IconButton
										size='small'
										aria-label={`Increase ${title} weight`}
										onClick={() => adjustWeight(key, WEIGHT_STEP)}
										disabled={!canEdit || weights[key] >= MAX_WEIGHT}
										sx={stepperButtonSx}
									>
										<Add sx={{ fontSize: 13 }} />
									</IconButton>

									<Typography
										sx={{ fontSize: '12px', color: 'text.secondary' }}
									>
										%
									</Typography>
								</Stack>
							</Box>
						))}

						<Divider />

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
								padding: '10px 12px',
							}}
						>
							<Typography
								sx={{
									fontSize: '12px',
									fontWeight: 600,
									color: 'text.primary',
								}}
							>
								Total
							</Typography>

							<Typography
								sx={{
									fontSize: '13px',
									fontWeight: 700,
									color: isTotalValid ? 'status.green.text' : 'status.red.text',
								}}
							>
								{total}%
							</Typography>
						</Box>

						{!isTotalValid && (
							<Box
								sx={{
									backgroundColor: 'status.red.bg',
									borderRadius: '7px',
									padding: '8px 12px',
								}}
							>
								<Typography
									sx={{
										fontSize: '11px',
										color: 'status.red.text',
									}}
								>
									{INVALID_TOTAL_MESSAGE}
								</Typography>
							</Box>
						)}
					</>
				)}
			</Box>

			<HealthScoreWeightingConfirmModal
				open={confirmOpen}
				onCancel={handleCancel}
				onConfirm={handleConfirmAndSave}
				saving={saving}
			/>
		</Box>
	);
};

export default HealthScoreWeighting;
